import { GAME_SETTINGS_KEY, MAP_LAYERS_KEY } from '@/config/storageKeys';
// 기본 환경설정(origin) — 신규 캐릭터·게스트가 「아무 설정 없이」 들어올 때 서는 자리.
// ============================================================================
// **이 파일이 정본이다.** 서버를 안 거친다 — 이 값은 브라우저가 렌더에 쓰고 localStorage 에
// 넣는 것이라 서버가 할 일이 없다.
//
// 원래는 DB(`game_config.ui_settings_origin`, 099)가 정본이고 여기는 비상 사본이었다.
// 근거는 「①룩을 바꿀 때 배포가 필요 없다 ②게스트와 신규 회원이 같은 값을 받는다」였는데
// 둘 다 죽었다 — ②는 애초에 논거가 아니었고(둘 다 이 상수를 읽으면 같은 값이다),
// ①은 거꾸로 됐다: 프론트는 계속 자라는데 DB 행이 8월 22일에 멈춰 **키 7개가 갈렸다**
// (`blockTexRes`·`farCity`·`farCityOffV1`·`hfRoadStepTarget`·`hfTopMottle`·`waterSnap` 이
// 신규 유저에게 안 실렸다). 「DB 가 있으면 프론트를 통째로 무시」하는 구조라 조용히 낡았다.
// 경위: docs/task/2026-09-06/2230_회차원장_개명과_죽은원장_드랍.md
//
// 저장소가 둘이라 값도 둘로 갈린다 — 합치면 어디에 써야 할지 잃는다:
//   game (localStorage 'game_settings_v1')  성능 탭 · 환경 탭의 빛/색보정
//   look (localStorage 'map_layers_v1')     지형 탭 hf* · 환경 탭 월드룩
//
// 사람이 읽는 사본: docs/image/settings/검증용_게스트_기본세팅.txt (스크린샷 5장 동봉)

const GAME_STORAGE_KEY = GAME_SETTINGS_KEY;
const LAYER_STORAGE_KEY = MAP_LAYERS_KEY;

/**
 * 성능 탭 + 환경 탭(빛·색보정).
 *
 * ⚠ 아래 빛·색 19개 값은 **LIGHT_PRESETS.storybook(「동화풍」)과 글자 그대로 같다.**
 *   같은 값을 두 벌 두는 게 아니라, 여기서 storybook 을 import 하면 useGameSettings ↔
 *   verifyPreset 이 서로를 부르는 순환이 된다. 그래서 리터럴로 박고 이 주석을 남긴다 —
 *   storybook 을 고치면 여기도 같이 고쳐야 한다(고치면 화면의 「동화풍」 칩이 꺼진다,
 *   그 칩은 저장된 키가 아니라 **값이 일치하는지로 역산**되기 때문이다).
 */
const ORIGIN_GAME = {
  // ── 마이그레이션 도장 ────────────────────────────────────────
  // 이 셋은 룩 값이 아니라 **「일회성 마이그레이션을 이미 했다」는 도장**이다. 왜 필요한가:
  // 그 마이그레이션들은 *저장된 옛 값*을 새 기준으로 끌어올리려고 있는 것인데, 기본값을
  // 막 받은 브라우저엔 끌어올릴 옛 값이 없다. 도장이 없으면 origin 을 써 넣자마자
  // 마이그레이션이 자기 기준값으로 통째로 덮어써서 — 실제로 그렇게 당했다(2026-08-22,
  // 게스트 입장 후 toneExposure 1.15 → 1.02, farTerrainStep 0 → 16).
  lightLookV2: true,
  lightLookV3: true,     // useGameSettings.applyLightLookV2 — storybook 한 벌 덮기
  farTerrainV1: true,    // applyFarTerrainV1 — 원경 지형 강제 ON 승격
  farTerrainV2: true,    // applyFarTerrainV2 — 2차 승격
  farCityOffV1: true,    // applyFarCityOffV1 — 원경 도시·나무 1회 끄기(아래 farCity 와 짝)
  originV1: true,        // 「이 값은 origin 에서 왔다」 — loadLocal 의 기기 마이그레이션이 이걸 보고 비켜선다

  // ── 성능: 그래픽 프리셋 · 세부 조정
  // 'custom' = 프리셋 칩(낮음·보통·높음)을 안 고른 상태. 아래 값들이 손으로 맞춘 조합이라
  // 어느 프리셋과도 안 맞고, 그걸 프리셋 이름으로 부르면 화면의 칩이 거짓말을 한다.
  gfxPreset: 'custom',
  dprScale: 1.0,
  // 블록 텍스처를 GPU 에 몇 픽셀로 올리나. 'auto' = 폰 256 / PC 512(`useBlockTextures`).
  // 파일은 언제나 1024 원본 한 벌뿐이고 축소는 실행 중에 한다 — 「폰만 다른 파일을 본다」가
  // 검은 화면을 만든 뒤로 경로 분기를 없앴다.
  blockTexRes: 'auto',
  farTerrainStep: 0,          // 원경 지형 끔 — 검증은 근경을 본다
  // 2026-08-23 되살림 — 끈 근거는 「구운 파일이라 세계보다 낡는다」였는데, 진입 때
  //   서버가 `world_chunk` 개정과 견줘 자동으로 다시 굽는다(`back/world/far_bake.py`).
  //   즉 **그 근거가 죽었다.** 옛 결정을 지우지 않고 왜 갈렸는지를 여기 남긴다.
  //   on/off 자체는 이제 LAYERS ▸ 원경 이 쥔다 — 이 값은 그 아래 단계다.
  farCity: true,
  vignetteEnabled: false,     // 가장자리를 어둡게 하면 두 컷의 밝기 비교가 흔들린다
  ambientLifeEnabled: true,

  // ── 성능: 빛 (= LIGHT_PRESETS.storybook)
  toneMapping: true,
  toneExposure: 1.02,
  sunIntensity: 1.30,
  ambientLift: 0.68,
  lightContrast: 0.60,
  shadeTint: 0.55,
  formLight: 0.60,
  macroVar: 0.60,

  // ── 성능: 그림자
  shadowsEnabled: true,
  groundShadow: true,
  terrainCastShadow: false,
  shadowStrength: 0.68,

  // ── 성능: 식생
  windStrength: 0,            // 0 은 의도다 — 흔들리면 프레임마다 그림이 달라져 A/B 가 깨진다
  propScale: 1.0,

  // ── 환경: 이펙트 (= LIGHT_PRESETS.storybook)
  bloomEnabled: true,
  bloomThreshold: 0.92,
  bloomIntensity: 0.50,
  gradeEnabled: true,
  gradeSplit: 0.85,
  gradeContrast: 0.30,
  gradeSat: 0.06,
  gradeVibrance: 0.34,
  gradeGreenTame: 0.40,
  gradeHighlight: 0.16,
  fogEnabled: true,
};

/** 지형 탭 hf* + 환경 탭 월드룩. 네 키(hfVoxStrata·hfVoxAO·hfVoxAlt·hfBandM)는 두 화면에 같이 뜬다. */
const ORIGIN_LOOK = {
  // 월드 룩 — 지형 질감
  hfVoxLip: 0.6,
  hfVoxStrata: 6.0,
  hfVoxAO: 0.95,
  hfVoxAlt: 1.5,
  hfBandM: 1,
  // 지면 소품층
  propDensity: 0.60,
  propDist: 55,
  propShadow: 0.50,
  // 지형 — 성능 / LOD
  hfNearR: 68,
  hfTileM: 12.0,
  hfTileR: 384,
  // 지형 — 형태
  hfVoxSub: 1,
  hfVoxSubSlope: 0.05,
  hfRoadSub: 2,
  hfRoadStepTarget: 0.9,
  hfCliffLo: 0.10,
  // 지형 — 표면 디테일
  hfDistrictAmp: 2.00,
  // ⚠ 바이옴 넷은 감사(audit_biome_*)가 재는 상수와 같아야 한다 — Hue = BIOME_HUE_K(0.6) · Sat 1 · Leaf 1 · Amp 1.
  //   더 세게 두면 자와 화면이 갈리고, 채도 2 는 청록 회전을 «형광»으로 밀어 누런 올리브가 된다.
  hfBiomeAmp: 1.00,
  hfBiomeHue: 0.60,
  hfBiomeSat: 1.00,
  hfBiomeRoad: 1.00,
  hfBiomeRock: 1.00,
  hfBiomeLeaf: 1.00,
  hfBiomeBlend: 150,
  hfBiomeMaterialBlend: 90,
  waterQuality: 1,
  waterVoxel: 4.00,
  waterSnap: 0.125,
  hfBlendRadius: 3.00,
  hfRelief: 0.00,
  hfFlattenSlope: 0.60,
  hfSteepBandMul: 1.00,
  hfEdgeSoft: 0.26,
  hfEdgeNoise: 0.76,
  hfTopMottle: 0.04,
  hfDetile: 1.00,
  hfFade: 1.00,
};

// useMapSettings 의 ONE_SHOT_MIGRATIONS 플래그(모듈 로드 때 한 번 확정된다).
// GameEntry 가 lazy 라 그 모듈은 `/game` 으로 갈 때 로드된다 — 로그인 시점에 미리 찍어 두면
// 「이미 한 것」으로 보고 비켜선다. 위 도장 주석과 같은 이유다.
const LOOK_MIGRATION_FLAGS = ['elev_on_v1', 'vox_strata_v2', 'blend_radius_v2', 'flatten_default_v1', 'road_sub_v1'];

const mergeInto = (key, values) => {
  try {
    const raw = localStorage.getItem(key);
    localStorage.setItem(key, JSON.stringify({ ...(raw ? JSON.parse(raw) : {}), ...values }));
    return true;
  } catch {
    return false;   // 사생활 모드 등 — 기본 룩이 안 서는 건 치명적이지 않다
  }
};

/**
 * 기본 환경설정을 두 저장소에 얹는다. **로그인 직후에만** 부른다(AuthContext).
 *
 * 왜 훅이 아니라 여기인가 — 순서다. 두 설정 스토어는 `/game` 라우트에서 **같은 커밋에**
 * 마운트하며 localStorage 를 한 번씩 읽는다(App.jsx:38 · useGameSettings.loadLocal ·
 * useMapSettings.loadLayers). 마운트 뒤에 덮으면 지형 쪽은 이미 옛 값을 읽은 뒤다.
 * 로그인은 그보다 앞선 화면에서 일어나므로, 여기서 써 두면 두 스토어가 처음부터 이 값으로
 * 선다 — 순서를 맞추려 애쓰는 대신 **더 앞에서 쓴다.**
 *
 * @param {{force?: boolean}} opts
 *   force=true  게스트. 계정 행이 없어 저장된 값이 있을 수 없다 — 매번 기준 룩으로 맞춘다.
 *   force=false 회원. **이 브라우저에 저장된 값이 하나도 없을 때만** 얹는다(신규 계정·새 기기).
 *               기존 값이 있으면 손대지 않는다 — 그건 유저가 고른 것이다.
 */
export async function applyOriginSettings({ force = false } = {}) {
  if (!force) {
    try {
      if (localStorage.getItem(GAME_STORAGE_KEY)) return false;   // 이미 자기 설정이 있다
    } catch { return false; }
  }

  try {
    for (const f of LOOK_MIGRATION_FLAGS) localStorage.setItem(f, '1');
  } catch { /* noop */ }
  const a = mergeInto(GAME_STORAGE_KEY, ORIGIN_GAME);
  const b = mergeInto(LAYER_STORAGE_KEY, ORIGIN_LOOK);
  return a && b;
}
